import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { combineLatest, Observable, of, timer } from 'rxjs';
import {
  catchError,
  delayWhen,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { ActivatedRoute } from '@angular/router';
import { RemoteDiagnosticsCloudApi } from '../../../projects/client';
import { RemoteDiagnosticsHelpersService } from '../../../projects/client/apis/remote-diagnostics/remote-diagnostics-helpers.service';
import { InstallationDetailsStore } from '../installation-details';
import { RemoteDiagnosticsStore } from '../remote-diagnostics';

import {
  AuthStore,
  BackupConfigurationResult,
  DiagStackDtcStore,
  DiagStackOtxStore,
  OtxBackupConfigurationParameters,
  OtxGenericInput,
  OtxGenericResponse,
  OtxGetDTCErrorSettingConditionParameters,
  OtxGetDTCErrorSettingConditionResponseStructure,
  OtxProcedureFileName,
  OtxProcedureName,
  OtxRestoreConfigurationParameters,
  RemoteDiagnosticsWebsocketResponse,
  RestoreConfigurationResult,
  VddDetailsListStore,
} from '..';
import { DiagnosticsWebsocketService } from '../../+installations/diagnostics-overlay/tabs-content/extended-parameters/diagnostics-websocket.service';
import { DiagStackOtxActions } from './diag-stack-otx.actions';
import { DiagStackOtxMappers } from './diag-stack-otx.service';

@Injectable()
export class DiagStackOtxEffects {
  executeOTX_GetDTCErrorSettingCondition_Attempt$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DiagStackOtxActions.executeOtx_GetDTCErrorSettingCondition.attempt),
      concatLatestFrom(() => [
        this.installationDetailsStore.gatewaySerial$,
        this.authStore.isDemo$,
      ]),
      // Delay is required in demo mode because we use active erros for listOfDTC
      // and during action dispatch the store isn't ready yet
      delayWhen(([_action, _gatewaySerial, isDemo]) => (isDemo ? timer(500) : timer(0))),
      mergeMap(([action, gatewaySerial]) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore.getDiagStackConnectionProperties(gatewaySerial).pipe(take(1)),
          this.remoteDiagnosticsStore.getMainLogicalLinkFromTopology(gatewaySerial).pipe(take(1)),
          this.remoteDiagnosticsStore.getNameOfSelectedActiveError(gatewaySerial).pipe(take(1)),
          this.diagStackDtcStore.getActiveDtcForSelectedError$.pipe(take(1)),
        ])
      ),
      switchMap(([action, diagStackConnectionProperties, logicalLink, errorName, dtc]) => {
        const sequenceModel: OtxGenericInput<OtxGetDTCErrorSettingConditionParameters> = {
          otxFileName: action.otxProcedureFileName,
          otxParameters: {
            curComChannel: {
              LogicalLink: logicalLink ?? dtc.logicalLink,
              Interface: diagStackConnectionProperties.vciName,
              Resource: diagStackConnectionProperties.vciResource,
              EcuVariantName: '',
            },
            listOfDTC: [errorName ?? dtc.dtcName],
          },
          otxProcedureName: action.otxProcedureName,
        };

        return this.remoteDiagnosticsCloudApi
          .diagnosticGenericOtxExecute<
            OtxGetDTCErrorSettingConditionParameters,
            OtxGenericResponse<OtxGetDTCErrorSettingConditionResponseStructure>
          >(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            sequenceModel
          )
          .pipe(
            map((otxGetDTCErrorSettingConditionResult) =>
              DiagStackOtxActions.executeOtx_GetDTCErrorSettingCondition.success({
                otxGetDTCErrorSettingConditionResult,
              })
            ),
            catchError((error) =>
              of(
                DiagStackOtxActions.executeOtx_GetDTCErrorSettingCondition.error({
                  otxGetDTCErrorSettingConditionError: error,
                })
              )
            ),
            takeUntil(this.remoteDiagnosticsHelpersService.cancelRequests$)
          );
      })
    );
  });

  triggerReadOfObtainedVddsByOtx$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          DiagStackOtxActions.executeOtx_GetDTCErrorSettingCondition.success,
          DiagStackOtxActions.triggerOtxVddsReadAgainAttempt
        ),
        mergeMap((action) =>
          combineLatest([
            of(action),
            this.diagStackOtxStore.getListOfVddFromOtxSequenceResult$.pipe(take(1)),
            this.diagStackDtcStore.getAllActiveDtcLogicalLink$.pipe(take(1)),
            this.installationDetailsStore.gatewaySerial$.pipe(take(1)),
          ])
        ),
        map(([_action, listOfDtcVdds, dtcLogicalLink, gatewaySerial]) => {
          listOfDtcVdds.forEach((dtcVdds) =>
            dtcVdds.vddNames.forEach((vddName) =>
              this.vddDetailsStore.getStructureAndValueForVddListAttempt(
                [vddName],
                gatewaySerial,
                dtcLogicalLink
              )
            )
          );
        })
      );
    },
    { dispatch: false }
  );

  getAvailableOtxSequences$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DiagStackOtxActions.getAvailableOtxSequences.attempt),
      concatLatestFrom(() => this.installationDetailsStore.gatewaySerial$),
      mergeMap(([_action, gatewaySerial]) =>
        combineLatest([
          of(gatewaySerial),
          this.remoteDiagnosticsStore.getXAuthTokenAndDiagStackNode(gatewaySerial).pipe(take(1)),
        ])
      ),
      switchMap(([gatewaySerial, xAuthTokenAndDiagStackNode]) =>
        this.remoteDiagnosticsCloudApi
          .diagnosticGenericOtxAvailableSequences(
            xAuthTokenAndDiagStackNode.node,
            xAuthTokenAndDiagStackNode.xAuthToken
          )
          .pipe(
            map((availableOtxSequences) =>
              DiagStackOtxActions.getAvailableOtxSequences.success({
                gatewaySerial,
                availableOtxSequences,
              })
            ),
            catchError((error) =>
              of(
                DiagStackOtxActions.getAvailableOtxSequences.error({
                  availableOtxSequencesError: error,
                })
              )
            ),
            takeUntil(this.remoteDiagnosticsHelpersService.cancelRequests$)
          )
      )
    );
  });

  triggerGetActiveDtc$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(DiagStackOtxActions.getAvailableOtxSequences.success),

        map((action) => {
          const isOtxProcedureNameAvailable = action.availableOtxSequences.find(
            (sequence) => sequence.name === OtxProcedureName.DTCErrorSettingCondition
          );
          if (isOtxProcedureNameAvailable) {
            return this.diagStackOtxStore.executeOtx_GetDTCErrorSettingCondition_Attempt(
              OtxProcedureName.DTCErrorSettingCondition,
              OtxProcedureFileName.DTCErrorSettingCondition
            );
          } else {
            return this.diagStackOtxStore.getAvailableOtxSequencesSequenceMissing();
          }
        })
      );
    },
    {
      dispatch: false,
    }
  );

  executeOTX_BackupConfiguration_Attempt$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DiagStackOtxActions.executeOtx_BackupConfiguration.attempt),
      mergeMap(() =>
        combineLatest([
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(this.getGatewaySerialFromRoute)
            .pipe(take(1)),
          this.remoteDiagnosticsStore
            .getMainLogicalLinkFromTopology(this.getGatewaySerialFromRoute)
            .pipe(take(1)),
        ])
      ),
      switchMap(([diagStackConnectionProperties, logicalLink]) => {
        const sequenceModel: OtxGenericInput<OtxBackupConfigurationParameters> = {
          otxFileName: OtxProcedureFileName.BackupAndRestore,
          otxProcedureName: OtxProcedureName.BackupConfiguration,
          otxParameters: {
            curComChannel: {
              LogicalLink: logicalLink,
              Interface: diagStackConnectionProperties.vciName,
              Resource: diagStackConnectionProperties.vciResource,
              EcuVariantName: '',
            },
            folderPath: '/opt/mcd3d/viessmann/logs', // discuss pods clean ups, only allowed for testing ( CANNOT STAY LIKE THIS ON PROD!!1! )
          },
        };

        return this.remoteDiagnosticsCloudApi
          .diagnosticGenericOtxExecute<OtxBackupConfigurationParameters, string>(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            sequenceModel
          )
          .pipe(
            map((response: string) =>
              this.diagStackOtxMappers.otxResponseParser<BackupConfigurationResult>(response)
            ),
            map((otxBackupConfigurationResult) =>
              DiagStackOtxActions.executeOtx_BackupConfiguration.success({
                otxBackupConfigurationResult,
              })
            ),
            catchError((error) =>
              of(
                DiagStackOtxActions.executeOtx_BackupConfiguration.error({
                  otxBackupConfigurationError: error,
                })
              )
            )
          );
      })
    );
  });

  executeOTX_BackupConfiguration_AttemptAsync$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DiagStackOtxActions.executeOtx_BackupConfiguration.attemptAsync),
      mergeMap(() =>
        combineLatest([
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(this.getGatewaySerialFromRoute)
            .pipe(take(1)),
          this.remoteDiagnosticsStore
            .getMainLogicalLinkFromTopology(this.getGatewaySerialFromRoute)
            .pipe(take(1)),
        ])
      ),
      switchMap(([diagStackConnectionProperties, logicalLink]) => {
        const sequenceModel: OtxGenericInput<OtxBackupConfigurationParameters> = {
          otxFileName: OtxProcedureFileName.BackupAndRestore,
          otxProcedureName: OtxProcedureName.BackupConfiguration,
          otxParameters: {
            curComChannel: {
              LogicalLink: logicalLink,
              Interface: diagStackConnectionProperties.vciName,
              Resource: diagStackConnectionProperties.vciResource,
              EcuVariantName: '',
            },
            folderPath: '/opt/mcd3d/viessmann/logs', // discuss pods clean ups, only allowed for testing ( CANNOT STAY LIKE THIS ON PROD!!1! )
          },
        };

        return this.remoteDiagnosticsCloudApi
          .diagnosticGenericOtxExecuteAsync<OtxBackupConfigurationParameters>(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            sequenceModel
          )
          .pipe(
            map((otxBackupConfigurationAsyncSchedulerId) =>
              DiagStackOtxActions.executeOtx_BackupConfiguration.invokeStatusCheck({
                otxBackupConfigurationAsyncSchedulerId,
              })
            ),
            catchError((error) =>
              of(
                DiagStackOtxActions.executeOtx_BackupConfiguration.error({
                  otxBackupConfigurationError: error,
                })
              )
            )
          );
      })
    );
  });

  executeOTX_BackupConfiguration_InvokeStatusCheck$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DiagStackOtxActions.executeOtx_BackupConfiguration.invokeStatusCheck),
      tap(() =>
        this.diagnosticsWebsocketService.initializeWebsocket(this.getGatewaySerialFromRoute)
      ),
      switchMap(() =>
        this.diagnosticsWebsocketService.webSocketValues.pipe(
          map(
            (
              otxBackupConfigurationResult: RemoteDiagnosticsWebsocketResponse<
                BackupConfigurationResult
              >
            ) =>
              DiagStackOtxActions.executeOtx_BackupConfiguration.success({
                otxBackupConfigurationResult: otxBackupConfigurationResult.data,
              })
          ),
          catchError((error) =>
            of(
              DiagStackOtxActions.executeOtx_BackupConfiguration.error({
                otxBackupConfigurationError: error,
              })
            )
          )
        )
      )
    );
  });

  executeOTX_RestoreConfiguration_Attempt$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DiagStackOtxActions.executeOtx_RestoreConfiguration.attempt),
      mergeMap(() =>
        combineLatest([
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(this.getGatewaySerialFromRoute)
            .pipe(take(1)),
          this.remoteDiagnosticsStore
            .getMainLogicalLinkFromTopology(this.getGatewaySerialFromRoute)
            .pipe(take(1)),
          this.diagStackOtxStore.getBackupListOfResultStructures$.pipe(take(1)),
        ])
      ),
      switchMap(([diagStackConnectionProperties, logicalLink, backupStructure]) => {
        const sequenceModel: OtxGenericInput<OtxRestoreConfigurationParameters> = {
          otxFileName: OtxProcedureFileName.BackupAndRestore,
          otxProcedureName: OtxProcedureName.RestoreConfiguration,
          otxParameters: {
            comChannel: {
              LogicalLink: logicalLink,
              Interface: diagStackConnectionProperties.vciName,
              Resource: diagStackConnectionProperties.vciResource,
              EcuVariantName: '',
            },
            filePath: '',
            restoreListOfResultStructures: backupStructure,
          },
        };

        return this.remoteDiagnosticsCloudApi
          .diagnosticGenericOtxExecute<OtxRestoreConfigurationParameters, string>(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            sequenceModel
          )
          .pipe(
            map((response: string) =>
              this.diagStackOtxMappers.otxResponseParser<RestoreConfigurationResult>(response)
            ),
            map((otxRestoreConfigurationResult) =>
              DiagStackOtxActions.executeOtx_RestoreConfiguration.success({
                otxRestoreConfigurationResult,
              })
            ),
            catchError((error) =>
              of(
                DiagStackOtxActions.executeOtx_RestoreConfiguration.error({
                  otxRestoreConfigurationError: error,
                })
              )
            ),
            takeUntil(this.remoteDiagnosticsHelpersService.cancelRequests$)
          );
      })
    );
  });

  executeOTX_RestoreConfiguration_AttemptAsync$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DiagStackOtxActions.executeOtx_RestoreConfiguration.attemptAsync),
      mergeMap(() =>
        combineLatest([
          this.remoteDiagnosticsStore
            .getDiagStackConnectionProperties(this.getGatewaySerialFromRoute)
            .pipe(take(1)),
          this.remoteDiagnosticsStore
            .getMainLogicalLinkFromTopology(this.getGatewaySerialFromRoute)
            .pipe(take(1)),
          this.diagStackOtxStore.getBackupListOfResultStructures$.pipe(take(1)),
        ])
      ),
      switchMap(([diagStackConnectionProperties, logicalLink, backupStructure]) => {
        const sequenceModel: OtxGenericInput<OtxRestoreConfigurationParameters> = {
          otxFileName: OtxProcedureFileName.BackupAndRestore,
          otxProcedureName: OtxProcedureName.RestoreConfiguration,
          otxParameters: {
            comChannel: {
              LogicalLink: logicalLink,
              Interface: diagStackConnectionProperties.vciName,
              Resource: diagStackConnectionProperties.vciResource,
              EcuVariantName: '',
            },
            filePath: '',
            restoreListOfResultStructures: backupStructure,
          },
        };

        return this.remoteDiagnosticsCloudApi
          .diagnosticGenericOtxExecuteAsync<OtxRestoreConfigurationParameters>(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            sequenceModel
          )
          .pipe(
            map((otxRestoreConfigurationAsyncSchedulerId) =>
              DiagStackOtxActions.executeOtx_RestoreConfiguration.invokeStatusCheck({
                otxRestoreConfigurationAsyncSchedulerId,
              })
            ),
            catchError((error) =>
              of(
                DiagStackOtxActions.executeOtx_RestoreConfiguration.error({
                  otxRestoreConfigurationError: error,
                })
              )
            )
          );
      })
    );
  });

  executeOTX_RestoreConfiguration_InvokeStatusCheck$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DiagStackOtxActions.executeOtx_RestoreConfiguration.invokeStatusCheck),
      tap(() =>
        this.diagnosticsWebsocketService.initializeWebsocket(this.getGatewaySerialFromRoute)
      ),
      switchMap(() =>
        this.diagnosticsWebsocketService.webSocketValues.pipe(
          map(
            (
              otxRestoreConfigurationResult: RemoteDiagnosticsWebsocketResponse<
                RestoreConfigurationResult
              >
            ) =>
              DiagStackOtxActions.executeOtx_RestoreConfiguration.success({
                otxRestoreConfigurationResult: otxRestoreConfigurationResult.data,
              })
          ),
          catchError((error) =>
            of(
              DiagStackOtxActions.executeOtx_RestoreConfiguration.error({
                otxRestoreConfigurationError: error,
              })
            )
          )
        )
      )
    );
  });

  constructor(
    private actions$: Actions,
    private installationDetailsStore: InstallationDetailsStore,
    private remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private remoteDiagnosticsCloudApi: RemoteDiagnosticsCloudApi,
    private diagStackOtxMappers: DiagStackOtxMappers,
    private vddDetailsStore: VddDetailsListStore,
    private diagStackOtxStore: DiagStackOtxStore,
    private diagStackDtcStore: DiagStackDtcStore,
    private remoteDiagnosticsHelpersService: RemoteDiagnosticsHelpersService,
    private authStore: AuthStore,
    private activatedRoute: ActivatedRoute,
    private diagnosticsWebsocketService: DiagnosticsWebsocketService<
      RestoreConfigurationResult | BackupConfigurationResult
    >
  ) {}

  get getGatewaySerialFromRoute(): string {
    return this.activatedRoute.snapshot.queryParams.gatewaySerial;
  }
}
